import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import {
  CREATE_EXPOSURE_CODE,
  CREATE_EXPOSURE_CODES,
  GET_EXPOSURE_CODE_CASE_COUNT,
  GET_EXPOSURES,
  MODIFY_EXPOSURE_CODE,
  MODIFY_EXPOSURE_TYPE,
} from '@app/constants/endpoints';
import { CASE_EXPOSURES } from '@app/constants/feature-definitions';
import { QUERY_KEYS } from '@app/constants/service';
import {
  deleteData,
  fetchData,
  patchData,
  postData,
} from '@app/hooks/react-query-helpers';
import {
  resolveFeatureEnabled,
  useEffectiveFeatures,
  useIsFeatureEnabled,
} from '@app/services/organization-features';

export interface ExposureCode {
  id: string;
  name: string;
  exposureTypeId: string;
}

export interface ExposureType {
  id: string;
  key?: string;
  name: string;
  restricted: boolean;
  codes: ExposureCode[];
}

export const useIsCaseExposuresEnabled = (): boolean =>
  useIsFeatureEnabled(CASE_EXPOSURES, { failClosed: true });

export const useCaseExposuresGate = (): {
  resolved: boolean;
  enabled: boolean;
} => {
  const { data, isLoading } = useEffectiveFeatures();
  return {
    resolved: !isLoading,
    enabled: resolveFeatureEnabled(data, CASE_EXPOSURES, { failClosed: true }),
  };
};

const EXPOSURES_QUERY_KEY = [QUERY_KEYS.EXPOSURES];

export const exposuresQuery = () => ({
  queryKey: EXPOSURES_QUERY_KEY,
  queryFn: async () => {
    const { types } = await fetchData(GET_EXPOSURES, { handleError: true });
    return types as ExposureType[];
  },
});

export const useExposures = ({ enabled = true } = {}): UseQueryResult<
  ExposureType[]
> => useQuery({ ...exposuresQuery(), enabled });

const useExposureMutation = <TVariables, TData = unknown>(
  mutationFn: (variables: TVariables) => Promise<TData>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    // Not returned: react-query awaits onSuccess, so returning the promise would make every
    // mutation in a batch wait on its own refetch and cancel the previous one.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXPOSURES_QUERY_KEY });
    },
  });
};

export interface MapCodeVariables {
  name: string;
  exposureTypeId: string;
}

export const useMapExposureCode = () =>
  useExposureMutation(({ name, exposureTypeId }: MapCodeVariables) =>
    postData(
      CREATE_EXPOSURE_CODE,
      { name, exposureTypeId },
      { handleError: true },
    ),
  );

export interface BulkMapCodesVariables {
  exposureTypeId: string;
  names: string[];
}

export interface BulkMapCodesResult {
  created: ExposureCode[];
  conflicts: { name: string; exposureType: string | null }[];
}

export const useBulkMapExposureCodes = () =>
  useExposureMutation(
    ({ exposureTypeId, names }: BulkMapCodesVariables) =>
      postData(CREATE_EXPOSURE_CODES(exposureTypeId), names, {
        handleError: true,
      }) as Promise<BulkMapCodesResult>,
  );

export interface ExposureCodeCaseCount {
  caseCount: number;
}

const CASE_COUNT_STALE_TIME_MS = 30_000;

export interface ExposureCodeCaseCounts {
  byCodeId: Record<string, number>;
  total: number;
  isReady: boolean;
  isError: boolean;
}

export const useExposureCodeCaseCounts = (
  codeIds: string[],
  { enabled = true }: { enabled?: boolean } = {},
): ExposureCodeCaseCounts => {
  const results = useQueries({
    queries: codeIds.map((codeId) => ({
      queryKey: [...EXPOSURES_QUERY_KEY, codeId, 'case-count'],
      queryFn: (): Promise<ExposureCodeCaseCount> =>
        fetchData(GET_EXPOSURE_CODE_CASE_COUNT(codeId), { handleError: true }),
      staleTime: CASE_COUNT_STALE_TIME_MS,
      enabled,
    })),
  });

  const byCodeId: Record<string, number> = {};
  codeIds.forEach((codeId, index) => {
    const count = results[index]?.data?.caseCount;
    if (typeof count === 'number') byCodeId[codeId] = count;
  });

  return {
    byCodeId,
    total: Object.values(byCodeId).reduce((sum, count) => sum + count, 0),
    isReady: results.length > 0 && results.every(({ isSuccess }) => isSuccess),
    isError: results.some(({ isError }) => isError),
  };
};

export interface RecategoriseCodeVariables {
  id: string;
  exposureTypeId: string;
}

interface EnqueuedRebuild {
  rebuildId?: string;
}

type RecategorisedCode = ExposureCode & EnqueuedRebuild;

// Deliberately unguarded, unlike delete. Affected cases are rebuilt in the background.
export const useRecategoriseExposureCode = () =>
  useExposureMutation<RecategoriseCodeVariables, RecategorisedCode | null>(
    ({ id, exposureTypeId }) =>
      patchData(
        MODIFY_EXPOSURE_CODE(id),
        { exposureTypeId },
        { handleError: true },
      ),
  );

export const useDeleteExposureCode = () =>
  useExposureMutation((id: string) =>
    deleteData(MODIFY_EXPOSURE_CODE(id), { handleError: true }),
  );

export interface SetRestrictionVariables {
  id: string;
  restricted: boolean;
}

type UpdatedExposureType = Omit<ExposureType, 'codes'> & EnqueuedRebuild;

export const useSetExposureTypeRestriction = () =>
  useExposureMutation<SetRestrictionVariables, UpdatedExposureType | null>(
    ({ id, restricted }) =>
      patchData(
        MODIFY_EXPOSURE_TYPE(id),
        { restricted },
        { handleError: true },
      ),
  );
